import { Select, NumberInput, Grid, Radio, Flex, ScrollArea, createStyles, Popover, ActionIcon, Text, TextInput } from "@mantine/core";
import { observer } from "mobx-react";
import { useParams } from "react-router-dom";
import { DependentOption, InputElement, Keys, QueryKeys } from "../../models/models";
import { useQuery, useStore } from "../../stores/store";
import theme from "../../stores/theme";
import { getSortedData } from "./configure-formula";
import { Icon } from "../../custom-components/icons";
import { faInfoCircle } from "@fortawesome/pro-duotone-svg-icons";
import { useState } from "react";
import t from "../../stores/language";

interface InputSelectorProps {
  span: number;
}

const useStyles = createStyles(() => ({
  configuredInputs: {
    ".mantine-InputWrapper-label": {
      display: "flex",
      alignItems: "center",
      height: "28px",
    },
  },
  inputHelp: {
    ".mantine-Popover-dropdown": {
      minWidth: 200,
      maxWidth: 500,
    },
    ".mantine-ActionIcon-root": {
      justifyContent: "flex-start",
    },
  },
  inputGroup: {
    fontSize: "14px",
    fontWeight: 500,
  },
}));

const InputSelector = observer(function InputSelector({ span }: InputSelectorProps) {
  const [showInputHelp, setShowInputHelp] = useState<{ id: string; show: boolean }>({ id: "", show: false });
  const { classes } = useStyles();
  const { store } = useStore();
  const { id } = useParams();
  const inputElements = store.GetInputByModule(id ?? "");
  const inputElementsOrder = (store.GetModuleById(id ?? "0")?.moduleConfig?.inputElementsOrder ?? []).map((ie) => ie.toString());
  const orderedInputElements = inputElementsOrder.length > 0 ? (getSortedData(inputElementsOrder, inputElements) as InputElement[]) : inputElements;
  const orderedGroups = [...new Set(orderedInputElements.map((input) => input.inputConfig?.group ?? ""))];
  const selectorValue = store.selectedInputs;
  const { data: lookupDataPageNo } = useQuery({
    queryKey: [QueryKeys.LookupDataPageNo],
    queryFn: async () => {
      return await store.GetAllLookupDataPageNo();
    },
  });
  const selectorValueChange = (id: number, value: string | number | null) => {
    store.UpdateSelectedInputs({ ...store.selectedInputs, [id]: value });
    if (value) {
      delete store.errorMessages[id];
    }
  };

  const isMatch = (dependentColumnValues: (string | number | null)[], key: Keys, selectedValues?: (string | number | null)[]) => {
    if (selectedValues) {
      // Gets value inside square brackets
      const dependentValues = key.value.match(/(?<=\[)[^\][]*(?=])/g);
      // Checks every values in selected values from dependent dropdown
      const exists = dependentValues?.every((m) => selectedValues.includes(m)) ?? false;
      return exists;
    }
    // To find key value in selected dependent values
    return dependentColumnValues.find((d) => d?.toString() === key.value) != null ? true : false;
  };

  const getDependentOptions = (dependentOptions: DependentOption[], dependentColumn: string[], id: number) => {
    const dependentColumnValues = selectorValue ? dependentColumn.map((column) => selectorValue[column]) : [];
    let options: string[] = [];
    // To get matched dependent option from multiple dependent dropdown options
    const dependent = dependentOptions?.find((d) =>
      d.keys.every((d) =>
        d.value.includes("[") && selectorValue ? isMatch(dependentColumnValues, d, Object.values(selectorValue)) : isMatch(dependentColumnValues, d)
      )
    );
    if (dependent != null) {
      options = dependent?.options ?? [];
    }
    if (selectorValue != null && selectorValue[id] && !options.includes(selectorValue[id]?.toString() ?? "")) {
      selectorValueChange(id, "");
    }
    return options;
  };

  const getInputLabel = (input: InputElement) => {
    return (
      <Flex align="center" className={classes.inputHelp}>
        <Popover position="top" withArrow shadow="md" opened={showInputHelp.id === input.id.toString() ? showInputHelp.show : false}>
          <Popover.Target>
            <ActionIcon
              onMouseEnter={() => setShowInputHelp({ id: input.id.toString(), show: true })}
              onMouseLeave={() => setShowInputHelp({ id: input.id.toString(), show: false })}
            >
              <Icon icon={faInfoCircle}></Icon>
            </ActionIcon>
          </Popover.Target>
          <Popover.Dropdown>
            <Text size="sm">{input.inputConfig?.inputHelp ?? ""}</Text>
          </Popover.Dropdown>
        </Popover>
        {input.inputConfig?.displayName ?? input.name}
      </Flex>
    );
  };

  const getInputConfigs = (input: InputElement) => {
    const errorMessage = store.errorMessages[input.id];
    switch (input.type) {
      case "Dependent Dropdown":
        return (
          <>
            <Select
              className={classes.configuredInputs}
              required
              label={input.inputConfig?.inputHelp ? getInputLabel(input) : input.inputConfig?.displayName ?? input.name}
              value={selectorValue && selectorValue[input.id] ? (selectorValue[input.id] as string) : ""}
              onChange={(value: string) => selectorValueChange(input.id, value)}
              data={getDependentOptions(input.inputConfig?.dependentOptions ?? [], input.inputConfig?.dependentColumn ?? [], input.id) ?? []}
              error={errorMessage}
            />
          </>
        );
      case "Dropdown":
        return (
          <>
            <Select
              className={classes.configuredInputs}
              required
              label={input.inputConfig?.inputHelp ? getInputLabel(input) : input.inputConfig?.displayName ?? input.name}
              defaultValue={input.default}
              value={selectorValue && selectorValue[input.id] ? (selectorValue[input.id] as string) : ""}
              onChange={(value: string) => selectorValueChange(input.id, value)}
              data={input.inputConfig?.options ?? []}
              error={errorMessage}
            />
          </>
        );
      case "Number Input":
        return (
          <>
            <NumberInput
              className={classes.configuredInputs}
              required
              defaultValue={input.default != null || input.default != "" ? Number(input.default) : 0}
              value={selectorValue && selectorValue[input.id] ? Number(selectorValue[input.id]) : undefined}
              label={input.inputConfig?.inputHelp ? getInputLabel(input) : input.inputConfig?.displayName ?? input.name}
              onChange={(value: number) => selectorValueChange(input.id, value)}
              max={input.inputConfig?.max}
              min={input.inputConfig?.min != null ? input.inputConfig?.min - 0.00001 : undefined}
              precision={theme.other?.numberInput.precision}
              step={theme.other?.numberInput.step}
              error={errorMessage}
            />
          </>
        );
      case "Radio":
        return (
          <>
            <Radio.Group
              className={classes.configuredInputs}
              required
              value={selectorValue && selectorValue[input.id] ? (selectorValue[input.id] as string) : input.default}
              onChange={(value: string) => selectorValueChange(input.id, value)}
              label={input.inputConfig?.inputHelp ? getInputLabel(input) : input.inputConfig?.displayName ?? input.name}
              error={errorMessage}
            >
              <ScrollArea>
                <Flex direction={"column"} wrap="nowrap" mah={70} gap={theme.other?.arc.paddings[1]}>
                  {input.inputConfig?.options?.map((option, index) => (
                    <Radio key={index} value={option} label={option} />
                  ))}
                </Flex>
              </ScrollArea>
            </Radio.Group>
          </>
        );
      case "Year Input":
        return (
          <Select
            onChange={(value) => {
              selectorValueChange(input.id, value ?? "");
            }}
            clearable
            value={selectorValue && selectorValue[input.id] ? (selectorValue[input.id] as string) : null}
            data={lookupDataPageNo ? Object.keys(lookupDataPageNo).sort((a, b) => parseInt(a) - parseInt(b)) : []}
            label={input.inputConfig?.inputHelp ? getInputLabel(input) : input.inputConfig?.displayName ?? input.name}
            searchable
            error={errorMessage}
            nothingFound={t("No year found")}
            maxDropdownHeight={150}
          />
        );
      case "Text Input":
        return (
          <TextInput
            className={classes.configuredInputs}
            required
            defaultValue={input.default ?? ""}
            value={selectorValue?.[input.id] ?? ""}
            label={input.inputConfig?.inputHelp ? getInputLabel(input) : input.inputConfig?.displayName ?? input.name}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => selectorValueChange(input.id, event.currentTarget.value)}
            error={errorMessage}
          />
        );
      default:
        return <></>;
    }
  };
  return (
    <Grid>
      {orderedGroups.map((group, i) => (
        <Grid.Col span={12} key={i}>
          <fieldset style={{ border: group != "" ? `0.0625rem solid ${theme.other?.arc.colors[5]}` : "none" }}>
            <legend className={classes.inputGroup}>{group}</legend>
            <Grid>
              {orderedInputElements
                .filter((input) => (group != "" ? input.inputConfig?.group === group : input.inputConfig?.group == null || input.inputConfig?.group === ""))
                .map((ie) => (
                  <Grid.Col key={ie.id} span={span}>
                    {getInputConfigs(ie)}
                  </Grid.Col>
                ))}
            </Grid>
          </fieldset>
        </Grid.Col>
      ))}
    </Grid>
  );
});
export default InputSelector;
